/**
 * 富编辑器选区、插入、遍历与提取（原 tooltipExtraRichEditorDom 后半部分）
 */
import { ITooltipExtraItem } from '../types';
import {
  CARET_ANCHOR,
  createFieldTagElement,
  mergeTextItems,
  normalizeEditorText,
  sanitizeMergedSubmitItems,
} from './richEditorTextAndField';

/**
 * 若选区落在字段标签内部，改为「标签之后」的折叠选区，避免插入点跑进 contentEditable=false。
 * 无选区或选区不在编辑器内时，退化为编辑器末尾。
 */
export const getNormalizedSelectionRange = (
  editorElement: HTMLDivElement,
): Range => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    const endRange = document.createRange();
    endRange.selectNodeContents(editorElement);
    endRange.collapse(false);
    return endRange;
  }
  const originRange = selection.getRangeAt(0);
  if (!editorElement.contains(originRange.commonAncestorContainer)) {
    const endRange = document.createRange();
    endRange.selectNodeContents(editorElement);
    endRange.collapse(false);
    return endRange;
  }
  const containerNode =
    originRange.startContainer.nodeType === Node.ELEMENT_NODE
      ? (originRange.startContainer as Element)
      : originRange.startContainer.parentElement;
  const fieldTag = containerNode?.closest('.dbl-chart-tooltip-extra-field-tag');
  if (fieldTag && editorElement.contains(fieldTag)) {
    const safeRange = document.createRange();
    safeRange.setStartAfter(fieldTag);
    safeRange.collapse(true);
    return safeRange;
  }
  return originRange;
};

/** 去掉 ZWSP 等后若仅剩一个或多个换行符，则视为「仅换行占位」 */
export const isNewlineOnlyPlaceholder = (raw: string): boolean => {
  if (!raw.length) {
    return false;
  }
  const t = normalizeEditorText(raw);
  return t.length > 0 && /^\n+$/.test(t);
};

/**
 * 插入字段前：若当前折叠选区处只有换行文本占位，或指向单独 BR，
 * 先删掉该占位，再用字段标签替换，避免顺序变成「字段-换行-字段」。
 */
export const normalizeRangeBeforeFieldInsert = (
  editor: HTMLDivElement,
  range: Range,
): Range => {
  if (!range.collapsed) {
    return range.cloneRange();
  }
  const out = range.cloneRange();

  // 选区在文本节点内：删掉光标后的「整段」仅换行后缀
  if (out.startContainer.nodeType === Node.TEXT_NODE) {
    const tn = out.startContainer as Text;
    const text = tn.textContent || '';
    const from = out.startOffset;
    const suffix = text.slice(from);
    if (isNewlineOnlyPlaceholder(suffix)) {
      tn.deleteData(from, suffix.length);
      if (!tn.textContent) {
        const parent = tn.parentNode!;
        const next = tn.nextSibling;
        const prev = tn.previousSibling;
        parent.removeChild(tn);
        const r = document.createRange();
        if (next) {
          r.setStartBefore(next);
        } else if (prev) {
          r.setStartAfter(prev);
        } else {
          r.selectNodeContents(parent);
          r.collapse(false);
        }
        r.collapse(true);
        return r;
      }
      const r = document.createRange();
      r.setStart(tn, from);
      r.collapse(true);
      return r;
    }
    // 光标紧贴「下一段」仅换行文本：移除兄弟节点
    if (suffix.length === 0) {
      const next = tn.nextSibling;
      if (
        next?.nodeType === Node.TEXT_NODE &&
        isNewlineOnlyPlaceholder(next.textContent || '')
      ) {
        next.parentNode!.removeChild(next);
        const r = document.createRange();
        r.setStart(tn, tn.textContent?.length || 0);
        r.collapse(true);
        return r;
      }
    }
    return out;
  }

  // 选区在元素边界：下一子节点可能是仅换行文本或 BR
  if (out.startContainer.nodeType === Node.ELEMENT_NODE) {
    const el = out.startContainer as HTMLElement;
    if (el !== editor && !editor.contains(el)) {
      return out;
    }
    const idx = out.startOffset;
    const child = el.childNodes[idx];
    if (
      child?.nodeType === Node.TEXT_NODE &&
      isNewlineOnlyPlaceholder(child.textContent || '')
    ) {
      const next = child.nextSibling;
      el.removeChild(child);
      const r = document.createRange();
      if (next) {
        r.setStartBefore(next);
      } else {
        const prev = el.childNodes[idx - 1];
        if (prev) {
          r.setStartAfter(prev);
        } else {
          r.selectNodeContents(el);
          r.collapse(false);
        }
      }
      r.collapse(true);
      return r;
    }
    if (child?.nodeName === 'BR') {
      const next = child.nextSibling;
      el.removeChild(child);
      const r = document.createRange();
      if (next) {
        r.setStartBefore(next);
      } else {
        const prev = el.childNodes[idx - 1];
        if (prev) {
          r.setStartAfter(prev);
        } else {
          r.selectNodeContents(el);
          r.collapse(false);
        }
      }
      r.collapse(true);
      return r;
    }
  }

  return out;
};

/**
 * 换行插入的是「换行符 + ZWSP」；浏览器默认退格会各删一次。
 * 命中该模式时一次退格删两字符，并恢复光标。
 * @returns 是否已处理（true 则外层应 preventDefault）
 */
export const consumeBackspaceSyntheticNewline = (
  editorElement: HTMLDivElement,
): boolean => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) {
    return false;
  }
  const range = sel.getRangeAt(0);
  if (!range.collapsed || !editorElement.contains(range.startContainer)) {
    return false;
  }
  if (range.startContainer.nodeType !== Node.TEXT_NODE) {
    return false;
  }
  const tn = range.startContainer as Text;
  const off = range.startOffset;
  const s = tn.textContent || '';

  const placeCaret = (node: Text, offset: number): void => {
    const r = document.createRange();
    r.setStart(node, offset);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  };

  // 光标在「换行符 + ZWSP」之后
  if (off >= 2 && s[off - 2] === '\n' && s[off - 1] === CARET_ANCHOR) {
    tn.deleteData(off - 2, 2);
    placeCaret(tn, off - 2);
    return true;
  }
  // 光标夹在换行符与 ZWSP 之间
  if (off === 1 && s.length >= 2 && s[0] === '\n' && s[1] === CARET_ANCHOR) {
    tn.deleteData(0, 2);
    placeCaret(tn, 0);
    return true;
  }
  // 光标在本段开头，软换行在上一文本节点末尾
  if (off === 0 && tn.previousSibling?.nodeType === Node.TEXT_NODE) {
    const prev = tn.previousSibling as Text;
    const ps = prev.textContent || '';
    if (
      ps.length >= 2 &&
      ps[ps.length - 2] === '\n' &&
      ps[ps.length - 1] === CARET_ANCHOR
    ) {
      prev.deleteData(ps.length - 2, 2);
      placeCaret(prev, ps.length - 2);
      return true;
    }
  }

  return false;
};

/** 将折叠选区移到节点之前（必要时插入/补齐带 ZWSP 的文本节点） */
export const setCaretBeforeNode = (node: Node): void => {
  const { parentNode } = node;
  if (!parentNode) {
    return;
  }
  let previousNode = node.previousSibling;
  if (!previousNode || previousNode.nodeType !== Node.TEXT_NODE) {
    previousNode = document.createTextNode(CARET_ANCHOR);
    parentNode.insertBefore(previousNode, node);
  } else if (!(previousNode.textContent || '').includes(CARET_ANCHOR)) {
    previousNode.textContent = `${previousNode.textContent || ''}${CARET_ANCHOR}`;
  }
  const textNode = previousNode as Text;
  const offset = (textNode.textContent || '').length;
  const selection = window.getSelection();
  if (!selection) {
    return;
  }
  const range = document.createRange();
  range.setStart(textNode, offset);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

/** 将折叠选区移到节点之后（同上） */
export const setCaretAfterNode = (node: Node): void => {
  const { parentNode } = node;
  if (!parentNode) {
    return;
  }
  let nextNode = node.nextSibling;
  if (!nextNode || nextNode.nodeType !== Node.TEXT_NODE) {
    nextNode = document.createTextNode(CARET_ANCHOR);
    if (node.nextSibling) {
      parentNode.insertBefore(nextNode, node.nextSibling);
    } else {
      parentNode.appendChild(nextNode);
    }
  } else if (!(nextNode.textContent || '').includes(CARET_ANCHOR)) {
    nextNode.textContent = `${CARET_ANCHOR}${nextNode.textContent || ''}`;
  }
  const textNode = nextNode as Text;
  const selection = window.getSelection();
  if (!selection) {
    return;
  }
  const range = document.createRange();
  range.setStart(textNode, 0);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

/**
 * 深度优先遍历编辑器子树：文本、字段标签、BR → ITooltipExtraItem[]，再 merge + sanitize。
 */
export const extractSubmitItemsFromDom = (
  editorElement: HTMLDivElement | null,
): ITooltipExtraItem[] => {
  if (!editorElement) {
    return [];
  }
  const nextItems: ITooltipExtraItem[] = [];
  const visitNodes = (nodes: NodeListOf<ChildNode> | ChildNode[]): void => {
    Array.from(nodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const value = node.textContent || '';
        if (value) {
          nextItems.push({
            type: 'text',
            value,
          });
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      const element = node as HTMLElement;
      if (element.classList.contains('dbl-chart-tooltip-extra-field-tag')) {
        const value = element.dataset.value || '';
        if (!value) {
          return;
        }
        nextItems.push({
          type: 'field',
          value,
          label: element.dataset.label || value,
          tableCode: element.dataset.tableCode,
          column: element.dataset.column,
          key: element.dataset.key,
        });
        return;
      }
      if (element.tagName === 'BR') {
        nextItems.push({
          type: 'text',
          value: '\n',
        });
        return;
      }
      if (element.childNodes?.length) {
        visitNodes(element.childNodes);
      }
    });
  };
  visitNodes(editorElement.childNodes);
  return sanitizeMergedSubmitItems(mergeTextItems(nextItems));
};

/**
 * 用 items 完全重绘编辑器：清空 innerHTML，再追加 field 标签与文本节点。
 * syncEditorRef：为 true 期间表示程序化更新，供外层跳过 input 同步。
 */
export const renderEditorByItems = (
  editorElement: HTMLDivElement | null,
  items: ITooltipExtraItem[],
  syncEditorRef: { current: boolean; },
): void => {
  if (!editorElement) {
    return;
  }
  syncEditorRef.current = true;
  editorElement.innerHTML = '';
  const fragment = document.createDocumentFragment();
  items.forEach((item) => {
    if (item.type === 'field') {
      fragment.appendChild(createFieldTagElement(item));
      return;
    }
    fragment.appendChild(
      document.createTextNode(String(item.value || '')),
    );
  });
  editorElement.appendChild(fragment);
  window.setTimeout(() => {
    syncEditorRef.current = false;
    editorElement.focus();
    const selection = window.getSelection();
    const newRange = document.createRange();
    newRange.selectNodeContents(editorElement);
    newRange.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(newRange);
  }, 0);
};

/**
 * 在规范化后的选区处：先删选区内内容，再插入 node，光标移到插入节点之后。
 * 无选区时退化为 append 到编辑器末尾。
 */
export const insertNodeAtCursor = (
  editorElement: HTMLDivElement | null,
  node: Node,
): void => {
  if (!editorElement) {
    return;
  }
  editorElement.focus();
  const selection = window.getSelection();
  if (selection) {
    const range = getNormalizedSelectionRange(editorElement);
    range.deleteContents();
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    return;
  }
  editorElement.append(node);
  const newRange = document.createRange();
  newRange.selectNodeContents(editorElement);
  newRange.collapse(false);
  const fallbackSelection = window.getSelection();
  fallbackSelection?.removeAllRanges();
  fallbackSelection?.addRange(newRange);
};

/** 插入字段标签：在 insertNodeAtCursor 前先 normalizeRangeBeforeFieldInsert */
export const insertFieldTagAtCursor = (
  editorElement: HTMLDivElement | null,
  node: Node,
): void => {
  if (!editorElement) {
    return;
  }
  editorElement.focus();
  const selection = window.getSelection();
  let range = getNormalizedSelectionRange(editorElement);
  if (!editorElement.contains(range.commonAncestorContainer)) {
    return;
  }
  range = normalizeRangeBeforeFieldInsert(editorElement, range);
  range.deleteContents();
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  selection?.removeAllRanges();
  selection?.addRange(range);
};

/**
 * 若存在有效的 savedRange（如下拉打开前保存），先写回 selection 再插入字段；
 * 用于恢复被 Select 抢走的焦点对应的光标位置。
 */
export const insertFieldTagAtSavedOrCursor = (
  editorElement: HTMLDivElement | null,
  savedRange: Range | null,
  node: Node,
): void => {
  if (!editorElement) {
    return;
  }
  if (savedRange && editorElement.contains(savedRange.commonAncestorContainer)) {
    const selection = window.getSelection();
    if (selection) {
      try {
        selection.removeAllRanges();
        selection.addRange(savedRange.cloneRange());
      } catch {
        // Range 与当前 DOM 不一致时忽略
      }
    }
  }
  insertFieldTagAtCursor(editorElement, node);
};

/**
 * 折叠选区在「文本边界」或「元素子节点边界」时，沿兄弟链找最近的字段标签。
 * isBackward：true 表示退格/左箭头方向（向前找），false 为 Delete/右箭头。
 */
export const getAdjacentFieldTag = (
  editorElement: HTMLDivElement | null,
  isBackward: boolean,
): HTMLElement | null => {
  const selection = window.getSelection();
  if (!editorElement || !selection || selection.rangeCount === 0) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (!range.collapsed || !editorElement.contains(range.startContainer)) {
    return null;
  }

  const getNearestSiblingTag = (
    currentNode: ChildNode | null,
    backward: boolean,
  ): HTMLElement | null => {
    let pointer = currentNode;
    while (pointer) {
      if (
        pointer.nodeType === Node.ELEMENT_NODE &&
        (pointer as Element).classList.contains('dbl-chart-tooltip-extra-field-tag')
      ) {
        return pointer as HTMLElement;
      }
      // 非空文本或 BR 会阻断「跳过空片段找标签」
      if (pointer.nodeType === Node.TEXT_NODE && (pointer.textContent || '').length > 0) {
        return null;
      }
      if (
        pointer.nodeType === Node.ELEMENT_NODE &&
        (pointer as Element).tagName === 'BR'
      ) {
        return null;
      }
      pointer = backward ? pointer.previousSibling : pointer.nextSibling;
    }
    return null;
  };

  const { startContainer, startOffset } = range;
  if (startContainer.nodeType === Node.TEXT_NODE) {
    const textNode = startContainer as Text;
    const isAtBoundary = isBackward
      ? startOffset === 0
      : startOffset === textNode.length;
    if (!isAtBoundary) {
      return null;
    }
    return getNearestSiblingTag(
      isBackward ? textNode.previousSibling : textNode.nextSibling,
      isBackward,
    );
  }

  if (startContainer.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const container = startContainer as Element;
  const adjacentIndex = isBackward ? startOffset - 1 : startOffset;
  if (adjacentIndex < 0 || adjacentIndex >= container.childNodes.length) {
    return null;
  }
  const adjacentNode = container.childNodes[adjacentIndex];
  return getNearestSiblingTag(adjacentNode, isBackward);
};

/**
 * 插入软换行：使用「换行符 + CARET_ANCHOR」而非浏览器默认 <br>，与 extract/render 一致；
 * ZWSP 缓解字段旁单次 Enter 不折行的问题，提交时由 normalizeEditorText 去掉。
 */
export const insertPlainNewlineAtCaret = (
  editorElement: HTMLDivElement | null,
): void => {
  if (!editorElement) {
    return;
  }
  editorElement.focus();
  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  const range = getNormalizedSelectionRange(editorElement);
  if (!editorElement.contains(range.commonAncestorContainer)) {
    return;
  }

  if (!range.collapsed) {
    range.deleteContents();
  }

  selection.removeAllRanges();
  selection.addRange(range);

  const chunk = `\n${CARET_ANCHOR}`;
  const r = selection.rangeCount > 0 ? selection.getRangeAt(0) : range;

  if (r.startContainer.nodeType === Node.TEXT_NODE) {
    const tn = r.startContainer as Text;
    const off = r.startOffset;
    tn.insertData(off, chunk);
    const nextRange = document.createRange();
    nextRange.setStart(tn, off + chunk.length);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    return;
  }

  const tn = document.createTextNode(chunk);
  r.insertNode(tn);
  const nextRange = document.createRange();
  nextRange.setStart(tn, chunk.length);
  nextRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(nextRange);
};
